import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { ScrollReveal } from "@/components/ScrollReveal";
import { useScrollLinked } from "@/hooks/useScrollLinked";
import { useCounterParallax } from "@/hooks/useCounterParallax";
import { subscribeScroll } from "@/lib/scrollEngine";
import {
  LayoutDashboard,
  Users,
  AppWindow,
  Workflow,
  Bot,
  Plug,
  type LucideIcon,
} from "lucide-react";

const capabilityMeta: { icon: LucideIcon; tag: string }[] = [
  { icon: LayoutDashboard, tag: "saas" },
  { icon: Users, tag: "portal" },
  { icon: AppWindow, tag: "webapp" },
  { icon: Workflow, tag: "ops" },
  { icon: Bot, tag: "ai" },
  { icon: Plug, tag: "api" },
];

const ServicesSection = () => {
  const { t } = useTranslation();
  const items = t("services.items", { returnObjects: true }) as {
    title: string;
    description: string;
  }[];
  const capabilities = capabilityMeta.map((meta, i) => ({
    ...meta,
    title: items[i]?.title ?? "",
    description: items[i]?.description ?? "",
  }));
  const { ref: titleRef, progress } = useScrollLinked(0.9, 0.5);
  const { ref: parallaxRef, offset } = useCounterParallax<HTMLElement>();
  const [active, setActive] = useState(0);
  const rowRefs = useRef<Array<HTMLDivElement | null>>([]);

  // On mobile (no reliable hover) expand whichever row is closest to the viewport center.
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(max-width: 767px)");

    const update = () => {
      if (!mq.matches) return;
      const center = window.innerHeight / 2;
      let bestIdx = 0;
      let bestDist = Infinity;
      rowRefs.current.forEach((el, idx) => {
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const rowCenter = rect.top + rect.height / 2;
        const dist = Math.abs(rowCenter - center);
        if (dist < bestDist) {
          bestDist = dist;
          bestIdx = idx;
        }
      });
      setActive(bestIdx);
    };

    const unsub = subscribeScroll(update);
    mq.addEventListener("change", update);
    return () => {
      unsub();
      mq.removeEventListener("change", update);
    };
  }, []);




  return (
    <section ref={parallaxRef} id="services" className="pt-12 md:pt-16 pb-24 md:pb-32 bg-background relative overflow-hidden">
      <div className="absolute inset-0 tech-grid opacity-[0.15] pointer-events-none" />
      {/* Counter-scrolling ghost watermark */}
      <div
        aria-hidden
        className="pointer-events-none absolute top-1/2 -translate-y-1/2 left-0 right-0 select-none whitespace-nowrap text-center font-bold text-foreground/[0.06] leading-none"
        style={{
          fontSize: "clamp(8rem, 22vw, 22rem)",
          transform: `translateY(-50%) translateX(${offset * 12}%)`,
          willChange: "transform",
        }}
      >
        BUILD ANYTHING
      </div>
      <div className="container relative">

        <div ref={titleRef} className="max-w-2xl mb-14 md:mb-16">
          <ScrollReveal>
            <p className="mono-label mb-3">// what_we_build</p>
            <h2 className="text-[1.55rem] sm:text-3xl md:text-4xl lg:text-5xl font-bold text-foreground leading-tight">
              {t("services.title")}
            </h2>
            <div className="mt-6 h-[3px] w-24 bg-border rounded-full overflow-hidden">
              <div
                className="h-full rounded-full"
                style={{ width: `${progress * 100}%`, background: "var(--gradient-primary)" }}
              />
            </div>
          </ScrollReveal>
        </div>

        <ScrollReveal>
          <div className="flex items-center justify-between border-b border-border pb-3 mb-2">
            <span className="font-mono text-[11px] tracking-[0.2em] uppercase text-muted-foreground">
              capability_index
            </span>
            <span className="font-mono text-[11px] tracking-[0.2em] uppercase text-muted-foreground">
              {String(active + 1).padStart(2, "0")} / {String(capabilities.length).padStart(2, "0")}
            </span>
          </div>

          <div className="divide-y divide-border border-b border-border">
            {capabilities.map((cap, i) => {
              const isActive = active === i;
              return (
                <div
                  key={cap.title}
                  ref={(el) => (rowRefs.current[i] = el)}
                  data-index={i}
                  onMouseEnter={() => setActive(i)}
                  className="group relative block w-full text-left overflow-hidden"
                >
                  {/* active glow wash */}
                  <div
                    className={`absolute inset-0 pointer-events-none transition-opacity duration-500 ${
                      isActive ? "opacity-100" : "opacity-0"
                    }`}
                    style={{ background: "radial-gradient(circle at 0% 50%, hsl(var(--primary) / 0.12) 0%, transparent 55%)" }}
                  />
                  {/* active left bar */}
                  <span
                    className={`absolute left-0 top-0 h-full w-[3px] bg-accent origin-top transition-transform duration-500 ${
                      isActive ? "scale-y-100" : "scale-y-0"
                    }`}
                  />

                  {/* watermark icon */}
                  <cap.icon
                    className={`absolute right-4 md:right-8 top-1/2 -translate-y-1/2 text-accent pointer-events-none transition-[opacity,transform] duration-700 ${
                      isActive ? "opacity-[0.18] scale-100" : "opacity-0 scale-90"
                    }`}
                    style={{ width: 130, height: 130 }}
                    strokeWidth={1}
                  />

                  <div className="relative z-10 flex items-center gap-5 md:gap-8 py-6 md:py-7 pl-4 md:pl-7 pr-4 md:pr-8">
                    <span
                      className={`font-mono text-sm md:text-base tabular-nums transition-colors duration-500 ${
                        isActive ? "text-accent" : "text-muted-foreground"
                      }`}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </span>

                    <div className="flex-1 min-w-0">
                      <span
                        className={`font-mono text-[10px] md:text-[11px] tracking-[0.2em] uppercase transition-colors duration-500 ${
                          isActive ? "text-accent/80" : "text-muted-foreground"
                        }`}
                      >
                        /{cap.tag}
                      </span>
                      <h3
                        className={`font-bold leading-tight transition-[color,font-size] duration-500 ${
                          isActive
                            ? "text-foreground text-2xl md:text-4xl"
                            : "text-muted-foreground text-xl md:text-3xl md:group-hover:text-foreground"
                        }`}
                      >
                        {cap.title}
                      </h3>

                      {/* expanding description */}
                      <div
                        className={`grid transition-[grid-template-rows,opacity,margin] duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] ${
                          isActive ? "grid-rows-[1fr] opacity-100 mt-3" : "grid-rows-[0fr] opacity-0 mt-0"
                        }`}
                      >
                        <div className="overflow-hidden">
                          <p className="text-muted-foreground text-sm md:text-base leading-relaxed max-w-xl">
                            {cap.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
};

export default ServicesSection;
